/**
 * # Card Security Code Input
 * An input container for payment security code details.
 *
 * @example
 * new Spark.Payments.CardSecurityCode(el, {...});
 *
 * @module components/payments/card-security-code.js
 */

import BaseComponent from '../base';
import Popover from '../popover';

import mixin from '../../helpers/util/mixin';
import addClass from '../../helpers/dom/add-class';
import removeClass from '../../helpers/dom/remove-class';
import makeElement from '../../helpers/dom/make-element';

import messaging from '../../mixins/messaging';
import validation from '../../mixins/validation';

let onValidEvent;
let onInvalidEvent;

class CardSecurityCode extends BaseComponent {

  /**
   * CardSecurityCode constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    super(el, params);

    this._bindEventListenerCallbacks();
    this._addEventListeners();
    onValidEvent = document.createEvent('Event');
    onInvalidEvent = document.createEvent('Event');

    onValidEvent.initEvent('valid', true, true);
    onInvalidEvent.initEvent('invalid', true, true);
  }


  /**
   * Sets up references
   * @param {Element} el base DOM element
   * @param {Object} params
   */
  _cacheElements(el, params) {
    this.el = el;
    this.inputEl = el ? el.querySelector('input') : undefined;

    if (params.info) {
      this.infoPopoverEl = document.querySelector(params.info);
    }

    if (!this.el || !this.inputEl) {
      throw new Error('SecurityCode markup missing');
    } else {
      if (this.inputEl.value) {
        addClass(this.el, 'active');
      }

      if (this.infoPopoverEl) {
        const existingInfoButton = this.el.querySelector('.spark-payment__info--toggle');

        if (existingInfoButton) {
          this.infoEl = existingInfoButton;
        } else {
          this.infoToggleAriaLabel = params.infoToggleAriaLabel || 'Show Card Security Code instructions';
          this.infoEl = makeElement('<button aria-label="' + this.infoToggleAriaLabel + '" class="spark-payment__info--toggle spark-icon-question-mark-circle spark-icon--md"></button>');

          this.el.appendChild(this.infoEl);
        }

        this.infoPopover = new Popover(this.infoPopoverEl, {
          anchorX: 'center',
          anchorY: 'bottom',
          toggleEl: this.infoEl,
          onOpen: function() {
            addClass(this.infoEl, 'spark-icon--fill');
          }.bind(this),
          onClose: function() {
            removeClass(this.infoEl, 'spark-icon--fill');
            this.infoEl.focus();
          }.bind(this)
        });
      }

      this.validatePattern = this.validatePattern || this.inputEl.getAttribute('pattern');
      this.messageEl = this.el.querySelector('.spark-input__message') || makeElement('<span class="spark-input__message"></span>');
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onBlurBound = this._onBlur.bind(this);
    this._onFocusBound = this._onFocus.bind(this);
    this._onInputBound = this._onInput.bind(this);
    this._onInfoBound = this._onInfo.bind(this);
    this._onInvalidBound = this._onInvalid.bind(this);
    this._onValidBound = this._onValid.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   */
  _addEventListeners() {
    this.inputEl.addEventListener('input', this._onInputBound, false);
    this.inputEl.addEventListener('blur', this._onBlurBound, false);
    this.inputEl.addEventListener('focus', this._onFocusBound, false);
    this.inputEl.addEventListener('invalid', this._onInvalidBound, false);
    this.inputEl.addEventListener('valid', this._onValidBound, false);

    if (this.infoEl) {
      this.infoEl.addEventListener('click', this._onInfoBound, false);
      this.infoEl.addEventListener('blur', this._onInfoBlurBound, false);
    }
  }


  /**
   * Remove event listeners for DOM events..
   */
  _removeEventListeners() {
    this.inputEl.removeEventListener('input', this._onInputBound, false);
    this.inputEl.removeEventListener('blur', this._onBlurBound, false);
    this.inputEl.removeEventListener('focus', this._onFocusBound, false);
    this.inputEl.removeEventListener('invalid', this._onInvalidBound, false);
    this.inputEl.removeEventListener('valid', this._onValidBound, false);

    if (this.infoEl) {
      this.infoEl.removeEventListener('click', this._onInfoBound, false);
      this.infoEl.removeEventListener('blur', this._onInfoBlurBound, false);
    }
  }


  /**
   * Getter for Primary Component Value
   * @return {String}
   */
  get value() {
    return this.inputEl ? this.inputEl.value : undefined;
  }


  /**
   * Setter for Primary Component Value
   * @param {Number} val
   */
  set value(val) {
    if (this.inputEl) {
      if (!isNaN(val)) {
        addClass(this.el, 'active');
        this.inputEl.value = val;
      } else if (val === '' || val === undefined || val === null) {
        removeClass(this.el, 'active');
        this.inputEl.value = '';
      }
    }
  }


  /**
   * Allows adjustment of max length externaly, so that as card types are recognized by number,
   * this input may be updated to reflect the correct CVV length
   * @param {Number} length
   */
  set maxLength(length) {
    this.inputEl.maxLength = length && length >= 3 && length <= 4 ? length : 4;
  }


  /**
   * Getter for Valid state, returns true if component value is valid, false if not
   * @return {Boolean}
   */
  get valid() {
    let isValid = true;

    // Check against regex pattern
    if (this.validatePattern) {
      isValid = new RegExp(this.validatePattern).test(this.inputEl.value);
    }

    // Check against minlength if it is set
    if (this.inputEl.getAttribute('minlength')) {
      isValid = (this.inputEl.value.length >= this.inputEl.getAttribute('minlength') && isValid);
    }

    // Check against maxlength if it is set
    if (this.inputEl.getAttribute('maxlength')) {
      isValid = (this.inputEl.value.length <= this.inputEl.getAttribute('maxlength') && isValid);
    }

    return this.inputEl.willValidate && isValid;
  }


  /**
   * onBlur callback, for controlling component active state, firing provided callbacks, and running validation
   */
  _onBlur() {
    if (!this.value) {
      this.hide();
    }

    this.onBlur.call(this);

    if (this.inputEl.hasAttribute('data-novalidate')) {
      this.inputEl.removeAttribute('data-novalidate');
    }

    if (!this.valid) {
      this.inputEl.dispatchEvent(onInvalidEvent);
    }
  }


  /**
   * onFocus callback, for controlling component active state and firing provided callbacks
   */
  _onFocus() {
    this.show();
    this.onFocus.call(this);
  }


  /**
   * onInput callback, for controlling component active state, firing provided callbacks, and running validation
   */
  _onInput() {
    this.value = this.toString().replace(/\D/g, '');

    if (this.value) {
      this.show();
    }

    this.onInput.call(this);

    if (this.inputEl.hasAttribute('data-novalidate')) {
      return;
    } else {
      // Use default validation mixin to validate the value of the input if a pattern is provided
      if (this.validatePattern) {
        this.validate();
      }

      // Maintain existing validation events until deprecated and removed
      const validityEvent = this.valid ? onValidEvent : onInvalidEvent;
      this.inputEl.dispatchEvent(validityEvent);
    }
  }


  /**
   * onInfo callback, fired when optional info button addon is interacted with
   * either fires callback method, or displays popover
   * @param {Object} e
   */
  _onInfo(e) {
    e.preventDefault();
    e.stopPropagation();

    if (this.infoPopover) {
      this.infoPopover.open({
        affixTo: this.infoEl
      });
    }
  }


  /**
   * onInvalid method for firing provided callbacks
   */
  _onInvalid() {
    this.onInvalid.call(this);
  }


  /**
   * onValid method for firing provided callbacks
   */
  _onValid() {
    this.onValid.call(this);
  }


  /**
   * Method for triggering focus on component's input element from outside of component
   */
  focus() {
    this.inputEl.focus();
  }


  /**
   * Returns a string equivilent to the currently visible value of the input
   * @return {String}
   */
  toString() {
    return this.inputEl.value;
  }


  /**
   * Show the input by adding the active state and setting character counts (if necessary).
   */
  show() {
    addClass(this.el, 'active');
    return this;
  }


  /**
   * Hide the input by removing the active state.
   */
  hide() {
    removeClass(this.el, 'active');
    return this;
  }


  /**
   * Set the value of the input.
   * @param {Mixed} value
   */
  setValue(value) {
    this.value = value;

    if (value) {
      this.show();
    } else {
      this.hide();
    }

    return this;
  }


  /**
   * Get the value of the input.
   * @return {String}
   */
  getValue() {
    return this.value;
  }


  /**
   * Clear the value of the input.
   */
  clearValue() {
    this.value = '';
    return this;
  }


  /**
   * Disable the input.
   */
  disable() {
    this.inputEl.attributes.setNamedItem(document.createAttribute('disabled'));
    return this;
  }


  /**
   * Enable the input.
   */
  enable() {
    this.inputEl.removeAttribute('disabled');
    return this;
  }


  /**
   * Remove component from DOM.
   * @param {Boolean} leaveElement
   */
  remove(leaveElement) {
    return super.remove(leaveElement);
  }


  /**
   * Update the element in use and the position.
   * @param {Element} el
   */
  update(el) {
    return super.update(el);
  }

}

/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
CardSecurityCode.prototype._whitelistedParams = [
  'onInput',
  'onBlur',
  'onFocus',
  'onInvalid',
  'onValid',
  'validate',
  'onValidate',
  'validatePattern'
];

/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
CardSecurityCode.prototype.defaults = {
  el: null,
  inputEl: null,
  validatePattern: null,
  onInput: () => {},
  onBlur: () => {},
  onFocus: () => {},
  onInvalid: () => {},
  onValid: () => {},
  onValidate: () => {}
};

mixin(CardSecurityCode.prototype, messaging, validation);

export default CardSecurityCode;
